
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Mail, X } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { Dialog, DialogContent } from "@/components/ui/dialog";

interface AuthFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onShowPricing: () => void;
}

export function AuthForm({ open, onOpenChange, onShowPricing }: AuthFormProps) {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSent, setIsSent] = useState(false);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      console.log("Attempting to sign in with email:", email);
      // Hardcode the redirect URL to ensure consistency
      const redirectTo = 'https://studio.theplasticsfella.com/auth/callback';
      console.log("Using redirect URL:", redirectTo);
      
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: redirectTo,
        },
      });

      if (error) throw error;

      setIsSent(true);
      toast({
        title: "Check your email",
        description: "We sent you a magic link to sign in. Be sure to check your spam folder.",
      });
      console.log("Magic link sent successfully");
    } catch (error) {
      console.error("Sign in error:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error instanceof Error ? error.message : "There was a problem sending the magic link.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignUp = () => {
    onOpenChange(false); // Close auth dialog
    onShowPricing(); // Show pricing dialog
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px] p-8 bg-white rounded-2xl">
        <div className="flex justify-between items-start mb-8">
          <h2 className="text-[32px] font-semibold text-gray-900 leading-tight">Sign in</h2>
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => onOpenChange(false)}
            className="rounded-xl hover:bg-gray-100"
          >
            <X className="h-5 w-5 text-gray-400" />
          </Button>
        </div>

        {!isSent ? (
          <form onSubmit={handleSignIn} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-base text-gray-700">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="name@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-14 px-4 text-base text-gray-600 bg-gray-50 border-0 rounded-xl focus-visible:ring-2 focus-visible:ring-primary"
              />
            </div>
            <Button 
              type="submit" 
              className="w-full h-14 text-base font-medium bg-primary hover:bg-primary/90 rounded-xl"
              disabled={isLoading}
            >
              {isLoading ? (
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              ) : (
                "Continue"
              )}
            </Button>
            <p className="text-center text-gray-600">
              Don't have an account?{" "}
              <button
                type="button"
                onClick={handleSignUp}
                className="text-primary hover:underline font-medium"
              >
                Sign up
              </button>
            </p>
          </form>
        ) : (
          <div className="space-y-6 text-center">
            <div className="mx-auto w-12 h-12 flex items-center justify-center bg-gray-50 rounded-full">
              <Mail className="h-6 w-6 text-primary" />
            </div>
            <div className="space-y-2">
              <h3 className="text-xl font-medium text-gray-900">Check your email</h3>
              <p className="text-gray-600">
                We sent a magic link to<br />
                <span className="font-medium text-gray-900">{email}</span>
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              className="w-full h-14 text-base font-medium rounded-xl"
              onClick={() => {
                setEmail("");
                setIsSent(false);
              }}
            >
              Use a different email
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
